'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { toast } from 'sonner';

const previewLinks = [
  { id: 'prev-link-1', title: 'Latest YouTube Video', icon: '▶', color: 'bg-red-50 border-red-100 text-red-700' },
  { id: 'prev-link-2', title: 'Instagram Profile', icon: '📸', color: 'bg-pink-50 border-pink-100 text-pink-700' },
  { id: 'prev-link-3', title: 'Shop My Merch', icon: '🛍️', color: 'bg-amber-50 border-amber-100 text-amber-700' },
  { id: 'prev-link-4', title: 'Join Newsletter', icon: '💌', color: 'bg-violet-50 border-violet-100 text-violet-700' },
];

export default function DashboardProfilePreview() {
  const [copied, setCopied] = useState(false);

  const handleCopyUrl = () => {
    // In real app: copy actual profile URL to clipboard
    setCopied(true);
    toast?.success('Profile URL copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4 sticky top-6">
      {/* Profile URL card */}
      <div className="bg-card rounded-2xl p-5 border border-border card-shadow">
        <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-3">Your Bio Link</p>
        <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5 mb-3">
          <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <span className="text-sm font-500 text-foreground font-mono flex-1 truncate">biolink.app/priya.creates</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCopyUrl}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-600 transition-all duration-200 ${
              copied
                ? 'bg-success/10 text-success border border-success/20' :'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
            }`}
          >
            {copied ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy URL
              </>
            )}
          </button>
          <Link
            href="/public-profile-page"
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open
          </Link>
        </div>
      </div>

      {/* Mini preview */}
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider">Live Preview</p>
          <span className="flex items-center gap-1.5 text-xs font-600 text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse-soft" />
            Published
          </span>
        </div>

        <div className="bg-gradient-brand p-5 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center font-800 text-xl text-white mb-3">
            P
          </div>
          <p className="text-white font-700 text-base">Priya Sharma</p>
          <p className="text-white/70 text-xs mt-1 text-center">✨ Content Creator & Lifestyle Blogger</p>
          <div className="flex gap-2 mt-3">
            {['📸', '▶', '🐦']?.map((icon, i) => (
              <span
                key={`prev-social-${i}`}
                className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm"
              >
                {icon}
              </span>
            ))}
          </div>
        </div>

        <div className="p-3 space-y-2 bg-background">
          {previewLinks?.map((link) => (
            <div
              key={link?.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-600 ${link?.color}`}
            >
              <span>{link?.icon}</span>
              <span className="truncate">{link?.title}</span>
            </div>
          ))}
          <p className="text-center text-xs text-muted-foreground pt-1">
            Made with <span className="text-primary font-600">BioLink</span>
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          href="/template-selection"
          className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center group"
        >
          <span className="text-xl group-hover:scale-110 transition-transform">🎨</span>
          <span className="text-xs font-600 text-foreground">Change Template</span>
        </Link>
        <Link
          href="/create-profile"
          className="flex flex-col items-center gap-2 p-4 rounded-xl bg-card border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center group"
        >
          <span className="text-xl group-hover:scale-110 transition-transform">✏️</span>
          <span className="text-xs font-600 text-foreground">Edit Profile</span>
        </Link>
      </div>
    </div>
  );
}